import {checkTagNameExistsPath, checkTagStatePath, repositoryPath} from '@github-ui/paths'
import {GitBranchIcon, SearchIcon, XIcon} from '@primer/octicons-react'
import {ActionList, Button, Heading, IconButton, Spinner, TextInput} from '@primer/react'
import {startTransition, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import {CheckTagNameDialog} from './CheckTagNameDialog'
import {CreateTagDialog} from './CreateTagDialog'
import {checkTagExists} from './check-tag-exists'
import {createBranch} from './create-branch'
import {defaultTypes, LoadingFailed, type RefSelectorProps, type RefType, RefTypeTabs} from './RefSelector'
import {RefSelectorAnchoredOverlay} from './RefSelectorAnchoredOverlay'
import {RefSelectorFooter} from './RefSelectorFooter'
import {RefsList} from './RefsList'
import {type RefsState, useRefs} from './use-refs'
import {announce} from '@github-ui/aria-live'

import styles from './RefSelectorV1.module.css'

import {clsx} from 'clsx'

export function RefSelectorV1(props: RefSelectorProps) {
  const {
    ariaDescribedBy,
    ariaLabelledBy,
    ariaLabel,
    cacheKey,
    owner,
    repo,
    canCreate,
    canCreateTag = false,
    types,
    hotKey,
    onOpenChange,
    size,
    variant,
    getHref,
    onBeforeCreate,
    onRefTypeChanged,
    currentCommitish,
    onCreateError,
    onSelectItem,
    closeOnSelect,
    selectedRefType,
    customFooterItemProps,
    buttonClassName,
    buttonText,
    persistButtonText,
    buttonPrefix,
    showIcon,
    allowResizing,
    idEnding,
    useFocusZone,
    disabled,
    anchoredOverlayProps,
    renderAnchor,
    actionListDescription,
  } = props
  const [filterText, setFilterText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const createButtonRef = useRef<HTMLLIElement>(null)

  const normalizedSelectedRefType = selectedRefType === 'tree' ? 'branch' : selectedRefType
  const displayCommitish = selectedRefType === 'tree' ? currentCommitish.slice(0, 7) : currentCommitish

  const [overlayOpen, setOverlayOpen] = useState(false)
  const [focusTrapEnabled, setFocusTrapEnabled] = useState(true)
  const [showTagWarningDialog, setShowTagWarningDialog] = useState(false)
  const [showCreateTagDialog, setShowCreateTagDialog] = useState(false)
  const [pendingTag, setPendingTag] = useState<string | null>(null)
  const [refType, setRefType] = useState<RefType>(normalizedSelectedRefType ?? (types ?? defaultTypes)[0]!)
  const descriptionId = useId()

  const refsState = useRefs(cacheKey, owner, repo, refType, filterText, canCreate)

  const newRefName = filterText

  const path = repositoryPath({owner, repo, action: 'branches'})
  const checkTagPath = checkTagNameExistsPath({owner, repo})
  const tagValidationPath = checkTagStatePath({owner, repo})

  const createAndGoToBranch = useCallback(async () => {
    onBeforeCreate?.(newRefName)

    const result = await createBranch(path, newRefName, currentCommitish)

    if (!result.success) {
      onCreateError?.(result.error)
    } else if (getHref) {
      // The branch creation endpoint will redirect automatically, but not for
      // XHR requests. So we have to manually switch to the new branch, which
      // is fine.

      // Clear the cache since the new branch isn't always picked up otherwise.
      refsState.searchIndex?.clearLocalStorage()

      window.location.href = getHref(result.name)
    }
  }, [onBeforeCreate, newRefName, path, currentCommitish, getHref, onCreateError, refsState.searchIndex])

  const checkTagAndCreate = useCallback(async () => {
    if (await checkTagExists(checkTagPath, newRefName)) {
      setShowTagWarningDialog(true)
      // disable focus trap to switch focus to the dialog.
      setFocusTrapEnabled(false)
      return
    }

    setShowTagWarningDialog(false)
    setFocusTrapEnabled(false)
    createAndGoToBranch()
  }, [checkTagPath, newRefName, createAndGoToBranch, setShowTagWarningDialog])

  const changeRefType = useCallback(
    (newRefType: RefType) => {
      setRefType(newRefType)
      onRefTypeChanged?.(newRefType)
    },
    [setRefType, onRefTypeChanged],
  )

  function closeRefSelector() {
    setOverlayOpen(false)
  }

  const onSelect = useCallback(
    (selectedItemRef: string, selectedItemRefType: RefType) => {
      onSelectItem?.(selectedItemRef, selectedItemRefType)
      closeRefSelector()
    },
    [onSelectItem],
  )

  const handleCreate = useCallback(() => {
    if (refType === 'tag') {
      onSelect(newRefName, 'tag')
      closeRefSelector()
    } else {
      checkTagAndCreate()
    }
  }, [refType, newRefName, onSelect, checkTagAndCreate])

  const handleCreateTagFromDialog = useCallback(
    (tagName: string) => {
      // Only set as pending if it doesn't already exist in the refs
      const tagExists = refsState.refs.includes(tagName)
      if (!tagExists) {
        setPendingTag(tagName)
      }
      // Clear filter to show all tags including the new one
      setFilterText('')
      onSelect(tagName, 'tag')
      setShowCreateTagDialog(false)
    },
    [onSelect, setPendingTag, setFilterText, refsState.refs],
  )

  const handleOpenCreateTagDialog = useCallback(() => {
    setShowCreateTagDialog(true)
    closeRefSelector()
  }, [])

  return (
    <>
      <RefSelectorAnchoredOverlay
        ariaDescribedBy={actionListDescription ? descriptionId : ariaDescribedBy}
        ariaLabelledBy={ariaLabelledBy}
        ariaLabel={ariaLabel}
        refType={refType}
        displayCommitish={displayCommitish}
        focusTrapEnabled={focusTrapEnabled}
        preventClosing={showTagWarningDialog}
        size={size}
        variant={variant}
        onOpenChange={onOpenChange}
        hotKey={hotKey}
        inputRef={inputRef}
        overlayOpen={overlayOpen}
        onOverlayChange={setOverlayOpen}
        buttonClassName={buttonClassName}
        buttonText={buttonText}
        persistButtonText={persistButtonText}
        buttonPrefix={buttonPrefix}
        showIcon={showIcon}
        allowResizing={allowResizing}
        idEnding={idEnding}
        useFocusZone={useFocusZone}
        disabled={disabled}
        anchoredOverlayProps={anchoredOverlayProps}
        renderAnchor={renderAnchor}
      >
        <RefSelectorActionList
          filterText={filterText}
          displayCommitish={displayCommitish}
          onFilterChange={setFilterText}
          refType={refType}
          onRefTypeChange={changeRefType}
          refsState={refsState}
          onCreateError={props.onCreateError}
          showTagWarningDialog={showTagWarningDialog}
          setShowTagWarningDialog={setShowTagWarningDialog}
          onCreateRef={handleCreate}
          inputRef={inputRef}
          createButtonRef={createButtonRef}
          onSelectItem={onSelect}
          closeOnSelect={closeOnSelect}
          closeRefSelector={closeRefSelector}
          customFooterItemProps={customFooterItemProps}
          {...props}
          selectedRefType={refType}
          onOpenCreateTagDialog={handleOpenCreateTagDialog}
          pendingTag={pendingTag}
          canCreateTag={canCreateTag}
          descriptionId={descriptionId}
        />
      </RefSelectorAnchoredOverlay>
      {showTagWarningDialog && (
        <CheckTagNameDialog
          isOpen={showTagWarningDialog}
          onDismiss={() => {
            setShowTagWarningDialog(false)
            createButtonRef.current?.focus()
          }}
          onConfirm={createAndGoToBranch}
        />
      )}

      {showCreateTagDialog && (
        <CreateTagDialog
          onDismiss={() => setShowCreateTagDialog(false)}
          onCreate={handleCreateTagFromDialog}
          initialTagName={newRefName}
          tagValidationUrl={tagValidationPath}
        />
      )}
    </>
  )
}

interface RefSelectorActionListProps extends Omit<RefSelectorProps, 'cacheKey'> {
  filterText: string
  onFilterChange: (filterText: string) => void
  onRefTypeChange: (refType: RefType) => void
  refType: RefType
  refsState: RefsState
  selectedRefType: RefType
  showTagWarningDialog: boolean
  setShowTagWarningDialog: (showWarningDialog: boolean) => void
  onCreateRef: () => void
  closeRefSelector: () => void
  inputRef?: React.RefObject<HTMLInputElement | null>
  createButtonRef?: React.RefObject<HTMLLIElement | null>
  displayCommitish: string
  onOpenCreateTagDialog: () => void
  pendingTag: string | null
  canCreateTag: boolean
  descriptionId: string
}

function RefSelectorActionList({
  canCreate,
  currentCommitish,
  displayCommitish,
  defaultBranch,
  filterText,
  getHref,
  hideShowAll,
  onSelectItem,
  closeOnSelect,
  closeRefSelector,
  onFilterChange,
  onRefTypeChange,
  owner,
  selectedRefType,
  refsState,
  refType,
  repo,
  types,
  onCreateRef,
  inputRef,
  createButtonRef,
  customFooterItemProps,
  viewAllJustify,
  actionListHeadingText,
  actionListDescription,
  onOpenCreateTagDialog,
  pendingTag,
  canCreateTag,
  descriptionId,
}: RefSelectorActionListProps) {
  const {refs, showCreateAction, status} = refsState

  // Add pending tag at top of list if it doesn't already exist
  const displayRefs = useMemo(() => {
    if (refType === 'tag' && pendingTag && !refs.includes(pendingTag)) {
      return [pendingTag, ...refs]
    }

    return refs
  }, [refs, pendingTag, refType])

  function onFooterItemClick() {
    customFooterItemProps?.onClick?.()
    closeRefSelector()
  }

  // For screen reader users
  useEffect(() => {
    const refLabels = {
      branch: {single: 'branch', plural: 'branches'},
      tag: {single: 'tag', plural: 'tags'},
    } as const
    const {single, plural} = refLabels[refType]
    const results = `${displayRefs.length} ${displayRefs.length === 1 ? single : plural} found`
    // One second delay so that the results are announced after the user stops typing
    const timeoutID = setTimeout(() => {
      announce(results)
    }, 1000)
    return () => clearTimeout(timeoutID)
  }, [displayRefs, refType])

  return (
    <div className="py-2" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <div className={styles.RefSelectorContainer}>
        <div className={styles.RefSelectorInnerContainer}>
          <Heading as="h2" className={styles.RefSelectorHeading}>
            {actionListHeadingText ?? switchRefText(types ?? defaultTypes)}
          </Heading>
          <IconButton
            tooltipDirection="w"
            aria-label="Cancel"
            variant="invisible"
            icon={XIcon}
            onClick={closeRefSelector}
            className={styles.RefSelectorCloseButton}
          />
        </div>

        {actionListDescription && (
          <p
            id={descriptionId}
            className="color-fg-muted f5"
            style={{paddingLeft: '16px', paddingRight: '16px', paddingBottom: '8px'}}
          >
            {actionListDescription}
          </p>
        )}

        <RefTextFilter
          defaultText={filterText}
          refType={refType}
          canCreate={canCreate}
          onFilterChange={onFilterChange}
          ref={inputRef}
        />
      </div>
      <div
        style={{
          paddingBottom: showCreateAction && displayRefs.length === 0 ? 0 : 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        {(types ?? defaultTypes).length > 1 && (
          <div className="px-2 pb-2">
            <RefTypeTabs refType={refType} onRefTypeChanged={onRefTypeChange} className={styles.RefTypeTabs} />
          </div>
        )}

        {status === 'loading' || status === 'uninitialized' ? (
          <Loading refType={refType} />
        ) : status === 'failed' ? (
          <LoadingFailed refType={refType} />
        ) : displayRefs.length === 0 && (!showCreateAction || refType === 'tag') ? (
          <RefsZeroState />
        ) : (
          <RefsList
            ariaControls={refType === 'branch' ? 'branches' : 'tags'}
            filterText={filterText}
            refs={displayRefs}
            defaultBranch={refType === 'branch' ? defaultBranch : ''}
            currentCommitish={refType === selectedRefType ? currentCommitish : ''}
            getHref={getHref}
            onSelectItem={ref => {
              onSelectItem?.(ref, refType)
              if (closeOnSelect) closeRefSelector()
            }}
            pendingTag={pendingTag}
          />
        )}
      </div>
      <ActionList className="p-0">
        {showCreateAction && (
          <>
            {refType === 'branch' && refs.length > 0 && (
              <ActionList.Divider className={clsx('d-block mt-0', styles.Divider)} />
            )}
            <CreateRefAction
              displayCommitish={displayCommitish}
              newRefName={filterText}
              onCreateRef={onCreateRef}
              createButtonRef={createButtonRef}
              refType={refType}
            />
          </>
        )}

        {(!hideShowAll || customFooterItemProps) && (
          <ActionList.Divider className={clsx(`d-block ${showCreateAction ? 'mt-2' : 'mt-0'}`, styles.Divider)} />
        )}

        {!hideShowAll && (
          <ViewAllRefsAction
            justify={viewAllJustify}
            refType={refType}
            owner={owner}
            repo={repo}
            onClick={closeRefSelector}
          />
        )}

        {customFooterItemProps && <RefSelectorFooter {...customFooterItemProps} onClick={onFooterItemClick} />}
      </ActionList>

      {refType === 'tag' && canCreateTag && (
        <>
          <ActionList.Divider className={clsx('d-block mt-0', styles.Divider)} />
          <div className="px-2">
            <Button onClick={onOpenCreateTagDialog} block>
              Create new tag
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

interface RefTextFilterProps {
  refType: RefType
  canCreate: boolean
  onFilterChange: (filterText: string) => void
  defaultText: string
  ref?: React.Ref<HTMLInputElement>
}

function RefTextFilter({refType, canCreate, onFilterChange, defaultText, ref}: RefTextFilterProps) {
  const [localText, setLocalText] = useState(defaultText)
  const [previousDefaultText, setPreviousDefaultText] = useState(defaultText)
  // Sync from parent when controlled value changes (e.g., cleared externally)
  if (defaultText !== previousDefaultText) {
    setLocalText(defaultText)
    setPreviousDefaultText(defaultText)
  }

  const placeholder =
    refType === 'tag'
      ? canCreate
        ? 'Search or create a new tag'
        : 'Search'
      : canCreate
        ? 'Find or create a branch...'
        : 'Find a branch...'
  const ariaLabel = refType === 'tag' ? 'Filter tags' : 'Filter branches'
  return (
    <div className={styles.RefSelectorFilterContainer}>
      <TextInput
        aria-label={ariaLabel}
        leadingVisual={SearchIcon}
        value={localText}
        placeholder={placeholder}
        ref={ref}
        onInput={e => {
          const target = e.target
          if (!(target instanceof HTMLInputElement)) return // Make ts happy
          setLocalText(target.value)
          startTransition(() => {
            onFilterChange(target.value)
          })
        }}
        className={styles.RefSelectorInput}
      />
    </div>
  )
}

function switchRefText(types: RefType[]) {
  if (types.includes('branch') && types.includes('tag')) {
    return 'Switch branches/tags'
  } else if (types.includes('branch')) {
    return 'Switch branches'
  } else if (types.includes('tag')) {
    return 'Switch tags'
  }
}

function Loading({refType}: {refType: RefType}) {
  return (
    <div className={styles.LoadingContainer}>
      <Spinner size="medium" aria-label={`Loading ${refType === 'branch' ? 'branches' : 'tags'}...`} />
    </div>
  )
}

function RefsZeroState() {
  return <div className={styles.ZeroStateText}>Nothing to show</div>
}

interface ViewAllRefsActionProps {
  refType: string
  owner: string
  repo: string
  onClick?: () => void
  justify?: RefSelectorProps['viewAllJustify']
}

function ViewAllRefsAction({refType, owner, repo, onClick, justify = 'start'}: ViewAllRefsActionProps) {
  const type = refType === 'branch' ? 'branches' : 'tags'
  return (
    <ActionList.LinkItem
      role="link"
      href={repositoryPath({owner, repo, action: type})}
      onClick={onClick}
      className={styles.ViewAllRefsActionLink}
    >
      <div
        style={{
          justifyContent: justify,
        }}
        className={styles.ViewAllRefsActionText}
      >
        View all {type}
      </div>
    </ActionList.LinkItem>
  )
}

interface CreateRefActionProps {
  displayCommitish: string
  newRefName: string
  onCreateRef: () => void
  createButtonRef?: React.RefObject<HTMLLIElement | null>
  refType: RefType
}

function CreateRefAction({displayCommitish, newRefName, onCreateRef, createButtonRef, refType}: CreateRefActionProps) {
  if (refType === 'tag') {
    return null
  }

  return (
    <ActionList.Item role="button" onSelect={onCreateRef} ref={createButtonRef}>
      <GitBranchIcon className={styles.BranchOcticon} />
      <span>Create branch&nbsp;</span>
      <span className={styles.RefActionText}>{newRefName}</span>
      <span>&nbsp;from&nbsp;</span>
      <span className={styles.RefActionText}>{displayCommitish}</span>
    </ActionList.Item>
  )
}
